/**
 * Password Reset Help Page
 * Shows when password reset link was opened in different browser
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, AlertCircle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useLocation } from 'wouter';

export const PasswordResetHelp: React.FC = () => {
  const [, setLocation] = useLocation();
  const { resetPassword } = useSupabaseAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">Check Your Email</h2>
            <p className="text-purple-300 mb-4">
              We've sent a new password reset link to:
            </p>
            <p className="text-white font-medium mb-6">{email}</p>

            <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-white font-semibold mb-2">Important:</h3>
              <ol className="text-blue-200 text-sm space-y-2">
                <li>✅ Click the reset link <strong>in THIS browser</strong></li>
                <li>✅ Don't switch browsers or devices</li>
                <li>✅ Complete the reset within 1 hour</li>
              </ol>
            </div>

            <button
              onClick={() => setLocation('/login')}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition"
            >
              Back to Login
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <button
          onClick={() => setLocation('/login')}
          className="flex items-center gap-2 text-purple-300 hover:text-white mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </button>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              Password Reset Link Issue
            </h2>
            <p className="text-purple-300 text-sm">
              The reset link was opened in a different browser
            </p>
          </div>

          <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">Why did this happen?</h3>
            <p className="text-purple-200 text-sm mb-3">
              For security, password reset links must be opened in the same browser where you requested them. This prevents unauthorized password resets.
            </p>
            <p className="text-purple-200 text-sm">
              <strong>Common causes:</strong>
            </p>
            <ul className="text-purple-300 text-xs mt-2 space-y-1">
              <li>• Clicking the link in a mobile email app</li>
              <li>• Opening in a different browser</li>
              <li>• Using incognito/private mode</li>
              <li>• Clearing browser data</li>
            </ul>
          </div>

          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">Solution:</h3>
            <p className="text-blue-200 text-sm">
              Request a new password reset link from <strong>this browser</strong>, then click the email link <strong>in this same browser</strong>.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Your Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition"
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-start gap-2"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send New Reset Link
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
